import { useState, useEffect } from "react";
import { Check, X, UserCheck, Users, Settings, Shield, Eye, Edit, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AVAILABLE_MODULES = [
  { id: "projects", name: "Projects", description: "View and manage projects" },
  { id: "boq", name: "BOQ Management", description: "Bill of Quantities" },
  { id: "dpr", name: "DPR Entry", description: "Daily Progress Reports" },
  { id: "materials", name: "Materials", description: "Material inventory management" },
  { id: "workforce", name: "Workforce", description: "Workforce and attendance" },
  { id: "equipment", name: "Equipment", description: "Equipment and fuel tracking" },
  { id: "hr", name: "HR Management", description: "Employee and leave management" },
  { id: "contractors", name: "Contractors", description: "Contractor management" },
  { id: "progress", name: "Progress Tracking", description: "Project progress analysis" },
  { id: "analytics", name: "Analytics", description: "Advanced analytics and reports" },
  { id: "approvals", name: "Approvals", description: "User and DPR approvals" },
];

const ROLES = [
  "Engineer",
  "Site Engineer",
  "Project Director",
  "Project Manager",
  "Project Coordinator",
  "HR Manager",
  "Store Keeper",
  "Mechanical Engineer",
  "Management",
];

const UserApproval = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  
  // Module Permission Dialog
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [modulePermissions, setModulePermissions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        apiClient.get('/users/pending'),
        apiClient.get('/users/all').catch(() => ({ data: [] }))
      ]);
      setPendingUsers(pendingRes.data);
      setApprovedUsers(approvedRes.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const openPermissionDialog = (user, isNew = true) => {
    setSelectedUser(user);
    setSelectedRole(user.role || "Engineer");
    
    // Initialize permissions - for new users, give view access to all by default
    if (isNew || !user.modules || user.modules.length === 0) {
      setModulePermissions(
        AVAILABLE_MODULES.map(m => ({
          module_id: m.id,
          can_view: true,
          can_edit: false,
          can_approve: false
        }))
      );
    } else {
      // Load existing permissions
      setModulePermissions(
        AVAILABLE_MODULES.map(m => {
          const existing = user.modules.find(um => um.module_id === m.id);
          return existing || {
            module_id: m.id,
            can_view: false,
            can_edit: false,
            can_approve: false
          };
        })
      );
    }
    
    setPermissionDialogOpen(true);
  };

  const togglePermission = (moduleId, permType) => {
    setModulePermissions(prev => prev.map(p => {
      if (p.module_id === moduleId) {
        const updated = { ...p, [permType]: !p[permType] };
        // If turning off view, turn off edit and approve too
        if (permType === 'can_view' && !updated.can_view) {
          updated.can_edit = false;
          updated.can_approve = false;
        }
        // If turning on edit or approve, turn on view too
        if ((permType === 'can_edit' || permType === 'can_approve') && updated[permType]) {
          updated.can_view = true;
        }
        return updated;
      }
      return p;
    }));
  };

  const selectAllPermissions = (permType) => {
    setModulePermissions(prev => prev.map(p => ({
      ...p,
      [permType]: true,
      ...(permType !== 'can_view' ? { can_view: true } : {})
    })));
  };

  const handleApprovalWithModules = async () => {
    try {
      await apiClient.post('/users/approve-with-modules', {
        user_id: selectedUser.id,
        approved: true,
        role: selectedRole,
        module_permissions: modulePermissions
      });
      toast.success("User approved with module permissions");
      setPermissionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleUpdateModules = async () => {
    try {
      await apiClient.put(`/users/${selectedUser.id}/modules`, modulePermissions);
      toast.success("Module permissions updated");
      setPermissionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update permissions");
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm("Reject and remove this user?")) return;
    try {
      await apiClient.post('/users/approve', {
        user_id: userId,
        approved: false
      });
      toast.success("User rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject user");
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">USER MANAGEMENT</h1>
        <p className="text-slate-600 mt-2 font-body">Approve users and manage module access permissions</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("pending")}
          data-testid="tab-pending"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "pending" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <UserCheck className="w-4 h-4 inline mr-2" />
          Pending ({pendingUsers.length})
        </button>
        <button
          onClick={() => setActiveTab("approved")}
          data-testid="tab-approved"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "approved" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Approved Users ({approvedUsers.length})
        </button>
      </div>

      {/* Pending Users Tab */}
      {activeTab === "pending" && (
        <>
          {pendingUsers.length === 0 ? (
            <div className="text-center py-16 stat-card">
              <UserCheck className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="font-heading font-bold text-xl mb-2">No Pending Approvals</h3>
              <p className="text-slate-600">All user registrations have been reviewed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingUsers.map((user) => (
                <div key={user.id} className="stat-card" data-testid={`pending-user-${user.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-xl text-primary mb-1">{user.name}</h3>
                      <p className="text-slate-600 mb-2">{user.email}</p>
                      <div className="inline-block px-3 py-1 rounded-sm text-xs font-medium uppercase tracking-wide bg-slate-200 text-slate-700">
                        Requested: {user.role}
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => openPermissionDialog(user, true)}
                        data-testid={`configure-user-${user.id}`}
                        className="btn-primary flex items-center space-x-2"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Configure & Approve</span>
                      </button>
                      <button
                        onClick={() => handleReject(user.id)}
                        data-testid={`reject-user-${user.id}`}
                        className="btn-outline flex items-center space-x-2 border-error text-error hover:bg-error/10"
                      >
                        <X className="w-4 h-4" />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Approved Users Tab */}
      {activeTab === "approved" && (
        <div className="stat-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Name</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Email</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Role</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Modules</th>
                <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvedUsers.map(user => (
                <tr key={user.id} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium">{user.name}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                      user.role === 'Master Admin' ? 'bg-primary/20 text-primary' :
                      user.role === 'Management' ? 'bg-accent/20 text-accent' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="font-mono text-sm">
                      {user.role === 'Master Admin' || user.role === 'Management' 
                        ? 'All' 
                        : `${user.modules?.filter(m => m.can_view)?.length || 0} modules`}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {user.role !== 'Master Admin' && (
                      <button
                        onClick={() => openPermissionDialog(user, false)}
                        className="p-2 hover:bg-slate-200 rounded"
                        title="Edit Permissions"
                      >
                        <Settings className="w-4 h-4 text-primary" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {approvedUsers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              No approved users yet.
            </div>
          )}
        </div>
      )}

      {/* Module Permission Dialog */}
      <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {selectedUser?.approved ? 'EDIT MODULE PERMISSIONS' : 'CONFIGURE & APPROVE USER'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="mt-4 space-y-6">
              {/* User Info */}
              <div className="p-4 bg-slate-50 rounded-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-primary">{selectedUser.name}</h3>
                    <p className="text-slate-600 text-sm">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Assign Role</label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {ROLES.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => selectAllPermissions('can_view')}
                  className="btn-outline text-sm py-1"
                >
                  <Eye className="w-3 h-3 inline mr-1" /> Grant All View
                </button>
                <button
                  type="button"
                  onClick={() => selectAllPermissions('can_edit')}
                  className="btn-outline text-sm py-1"
                >
                  <Edit className="w-3 h-3 inline mr-1" /> Grant All Edit
                </button>
                <button
                  type="button"
                  onClick={() => selectAllPermissions('can_approve')}
                  className="btn-outline text-sm py-1"
                >
                  <CheckCircle className="w-3 h-3 inline mr-1" /> Grant All Approve
                </button>
              </div>

              {/* Module Permissions Grid */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Module Permissions</h4>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left py-2 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Module</th>
                        <th className="text-center py-2 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium w-24">
                          <Eye className="w-4 h-4 inline" /> View
                        </th>
                        <th className="text-center py-2 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium w-24">
                          <Edit className="w-4 h-4 inline" /> Edit
                        </th>
                        <th className="text-center py-2 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium w-24">
                          <Shield className="w-4 h-4 inline" /> Approve
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {AVAILABLE_MODULES.map((module, idx) => {
                        const perm = modulePermissions.find(p => p.module_id === module.id) || {};
                        return (
                          <tr key={module.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="py-2 px-4">
                              <div>
                                <p className="font-medium text-sm">{module.name}</p>
                                <p className="text-xs text-slate-500">{module.description}</p>
                              </div>
                            </td>
                            <td className="text-center py-2 px-4">
                              <button
                                type="button"
                                onClick={() => togglePermission(module.id, 'can_view')}
                                className={`w-8 h-8 rounded-sm transition-colors ${
                                  perm.can_view ? 'bg-success text-white' : 'bg-slate-200 text-slate-400'
                                }`}
                              >
                                <Check className="w-4 h-4 mx-auto" />
                              </button>
                            </td>
                            <td className="text-center py-2 px-4">
                              <button
                                type="button"
                                onClick={() => togglePermission(module.id, 'can_edit')}
                                className={`w-8 h-8 rounded-sm transition-colors ${
                                  perm.can_edit ? 'bg-accent text-white' : 'bg-slate-200 text-slate-400'
                                }`}
                              >
                                <Check className="w-4 h-4 mx-auto" />
                              </button>
                            </td>
                            <td className="text-center py-2 px-4">
                              <button
                                type="button"
                                onClick={() => togglePermission(module.id, 'can_approve')}
                                className={`w-8 h-8 rounded-sm transition-colors ${
                                  perm.can_approve ? 'bg-primary text-white' : 'bg-slate-200 text-slate-400'
                                }`}
                              >
                                <Check className="w-4 h-4 mx-auto" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={() => setPermissionDialogOpen(false)} className="btn-outline">
                  Cancel
                </button>
                <button
                  onClick={selectedUser.approved ? handleUpdateModules : handleApprovalWithModules}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>{selectedUser.approved ? 'Update Permissions' : 'Approve User'}</span>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserApproval;
